import { useState } from "react"
import { SkeletonLines } from "components/new-stuff/ui/Skeleton"
import { useGet } from "utils/useFetch"
import { dptKRs, IEmployeeKRListing } from "../../interface/department"
import { KR } from "pages/dashboard/performance/components/objectives/ObjCardComponents"
import PersonAvatar from "components/new-stuff/ui/PersonAvatar"
import { CreateEditKR } from "pages/dashboard/performance/components/objectives/CreateEditKR"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { Button, P2 } from "components"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { newArchAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import { ViewKR } from "pages/dashboard/performance/components/objectives/ViewKR"
import { useAppNotificationStore } from "stores/app-notification"

interface props {
  departmentObjectiveId: number | null
  pageType: "admin" | "manager" | "employee"
  KRURL: string
}

interface ModalState {
  type: "delete" | "edit" | "view" | ""
  data: dptKRs | null
}

const DepartmentKR = ({ departmentObjectiveId, pageType, KRURL }: props) => {
  const [modal, setModal] = useState<ModalState>({
    type: "",
    data: null,
  })
  const queryClient = useQueryClient()
  const { toast } = useAppNotificationStore()

  const { isLoading: isLoadingEmpKeyResults, data: EmpkeyResults } =
    useGet<IEmployeeKRListing>({
      url: `${KRURL}/${departmentObjectiveId}`,
      instanceKey: "newArchAxiosInstance",
      enabled: !!departmentObjectiveId,
    })

  const { isLoading: isDeletingOKR, mutate: deleteOKR } = useMutation({
    mutationFn: async (id: string) => {
      // /api/v{version}/manager/okr/departmentkeyresult/{departmentKeyResultId}

      const endpoint =
        pageType === "admin"
          ? `/api/v2/admin/okr/departmentkeyresult/${id}`
          : // ? `/api/v2/admin/okr/keyresult/${id}`
            `/api/v2/manager/okr/departmentkeyresult/${id}`

      const res = await newArchAxiosInstance.delete(endpoint)
      return res.data
    },
    onSuccess: (data) => {
      if (data.success) {
        setModal({
          type: "",
          data: null,
        })
        queryClient.invalidateQueries({
          queryKey: [
            `/api/v2/admin/okr/departmentkeyresult/${departmentObjectiveId}`,
          ],
        })
        queryClient.invalidateQueries({
          queryKey: [`${KRURL}/${departmentObjectiveId}`],
        })
        toast.success(data.message || "Operation successful")
      }
    },
    onError: onQueryError,
  })

  return (
    <div>
      {isLoadingEmpKeyResults ? (
        <div className="py-10" aria-busy="true">
          <SkeletonLines lines={4} />
        </div>
      ) : (
        <div className=" flex flex-col gap-2">
          {EmpkeyResults?.data && EmpkeyResults?.data?.length > 0 ? (
            EmpkeyResults?.data?.map((kr) => (
              <div key={`kr-${kr?.employeeKeyResultId}`}>
                <KR.KRItemWrapper loading={false}>
                  <KR.KRItemTitle title={kr?.keyResultTitle || ""} />

                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-4 justify-start mr-2">
                      <PersonAvatar
                        src={kr?.employeeTargetDto[0]?.employeeAvatarUrl}
                        alt=""
                        className="h-5 w-5"
                      />
                      <p className="text-padeBlack font-bold text-xs">
                        {kr?.employeeTargetDto[0]?.employeeName}
                      </p>
                    </div>

                    <KR.KRDueDate
                      date={kr?.employeeTargetDto[0]?.dueDate || ""}
                    />
                    <KR.KRWeight
                      weight={kr?.weight}
                      isWeightAssigned={kr?.isWeightAssigned}
                    />

                    <KR.KRProgress
                      progress={
                        kr?.employeeTargetDto[0]?.percentageProgress || 0
                      }
                    />

                    {pageType !== "employee" && (
                      <KR.KRActionsBtn
                        onDelete={() => {
                          setModal({
                            type: "delete",
                            data: kr,
                          })
                        }}
                        onEdit={() => {
                          setModal({
                            type: "edit",
                            data: kr,
                          })
                        }}
                        onView={() => {
                          setModal({
                            type: "view",
                            data: kr,
                          })
                        }}
                      />
                    )}
                  </div>
                </KR.KRItemWrapper>
              </div>
            ))
          ) : (
            <div className="w-full py-10 flex justify-center items-center">
              <h6 className=" text-padeBlack">No Key Result.</h6>
            </div>
          )}
        </div>
      )}

      {modal.type === "edit" && (
        <CreateEditKR
          onClose={() => {
            setModal({ type: "", data: null })
          }}
          open={true}
          role={pageType}
          krId={modal.data?.employeeKeyResultId}
          deptId={modal.data?.departmentId}
          deptObjId={modal.data?.departmentObjectiveId}
        />
      )}

      {modal.type === "view" && (
        <ViewKR
          onClose={() => {
            setModal({ type: "", data: null })
          }}
          open={true}
          data={modal.data as dptKRs}
          role={pageType}
        />
      )}

      <CustomModal
        open={modal.type === "delete"}
        onCancel={() => {
          setModal({ type: "", data: null })
        }}
        title={`Delete ${modal?.data?.keyResultTitle}`}
        footer={
          <div className="flex justify-end gap-4">
            <Button
              color="neutral"
              onClick={() => {
                setModal({ type: "", data: null })
              }}
              // disabled={isDeletingGroup}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={() => {
                deleteOKR(
                  modal?.data?.employeeKeyResultId?.toString() as string
                )
              }}
              disabled={isDeletingOKR}
              submitting={isDeletingOKR}
            >
              Delete
            </Button>
          </div>
        }
      >
        <P2 className=" font-normal  font-avenir text-neutral500">
          This key result will be permanently removed from the objective. You
          won't be able to undo this action.
        </P2>
      </CustomModal>
    </div>
  )
}

export default DepartmentKR
